import { findParentNodeClosestToPos } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { TIPTAP_COLLAPSIBLE_LIST_TYPE, TIPTAP_COLLAPSIBLE_ITEM_TYPE, TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE, TIPTAP_COLLAPSIBLE_ITEM_BODY_TYPE } from 'ricos-content';
import { TextStyle_TextAlignment } from 'ricos-schema';
import collapsibleListDataDefaults from 'ricos-schema/dist/statics/collapsible_list.defaults.json';
import { createRicosNodeExtension } from 'wix-rich-content-plugin-commons';
import { collapsibleStateManagerPlugin } from '../consts.js';
import { CollapsibleList, CollapsibleListItem, CollapsibleListItemTitle, CollapsibleListItemBody } from './component.js';
import { defaultCollapsibleItem } from './defaults.js';
import { jumpToCollapsibleBody, keyboardShortcuts } from './keyboardShortcuts.js';
import { updateCollapsibleListAttrs, updateCollapsibleListItemsAttrs, getCollapsibleListByItemId, findNodeById, getCollapsibleListItems, updateListItemAttributes, getAlignmentFromDirection } from './utils.js';
import '@tiptap/pm/state';
import 'ricos-types';
import 'classnames';
import 'react';
import 'ricos-context';
import '../collapsible-list-component.rtlignore-b2e49eb3.js';
import '../style-inject.es-e7b07dd7.js';
import '../collapsible-list-pair.rtlignore-d44a6457.js';
import '../components/ExpandCollapseButton.js';
import '../icons/ArrowIcon.js';
import '../components/NewPairButton.js';
import 'prop-types';
import '../dataHooks.js';
import '../icons/PlusIcon.js';
import '../icons/DndIcon.js';
import '../defaults.js';
import 'ricos-plugins';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const tiptapExtensions = [
  createRicosNodeExtension({
    groups: ["custom-styles", "shortcuts-enabled", "node-container", "footer-disabled"],
    type: "react-node",
    reconfigure: (config, extensions, _props, settings, services) => {
      const nodesWithTextContainer = extensions.filter((extension) => extension.groups.includes("text-container")).map(({ name }) => name);
      return __spreadProps(__spreadValues({}, config), {
        addOptions: () => {
          return __spreadValues({ services, nodesWithTextContainer }, settings);
        }
      });
    },
    Component: CollapsibleList,
    name: TIPTAP_COLLAPSIBLE_LIST_TYPE,
    createExtensionConfig({ Plugin }) {
      return {
        name: this.name,
        group: "block",
        selectable: true,
        draggable: true,
        priority: 25,
        content: `(${TIPTAP_COLLAPSIBLE_ITEM_TYPE})+`,
        addAttributes() {
          return __spreadProps(__spreadValues({}, collapsibleListDataDefaults), {
            direction: { default: this.options.services.context.languageDir.toUpperCase() },
            expandOnlyOne: { default: false },
            initialExpandedItems: { default: "FIRST" }
          });
        },
        addKeyboardShortcuts() {
          const newLine = jumpToCollapsibleBody;
          const tiptapKeyboardShortcuts = keyboardShortcuts;
          const newLineShortcuts = [];
          this.options.services.shortcuts.shortcuts.forEach((shortcut) => {
            if (shortcut.shortcut.name === "NewLine") {
              newLineShortcuts.push(shortcut);
            }
          });
          newLineShortcuts.forEach((newLineShortcut) => {
            const modifier = newLineShortcut.keys.macOs.modifiers[0];
            const key = newLineShortcut.keys.macOs.key;
            const shortcutKey = modifier ? `${modifier}-${key}` : key;
            tiptapKeyboardShortcuts[shortcutKey] = newLine;
          });
          return tiptapKeyboardShortcuts;
        },
        addCommands() {
          return {
            addCollapsibleListItem: (position) => ({ commands }) => {
              return commands.insertContentAt(position - 1, defaultCollapsibleItem);
            },
            setCollapsibleListAtributes: (nodeId, attrs) => ({ state, view, commands }) => {
              updateCollapsibleListAttrs(commands, nodeId, attrs);
              updateCollapsibleListItemsAttrs(state, commands, nodeId, attrs);
              view.dispatch(state.tr);
              return true;
            },
            toggleCollapsibleItem: (nodeId) => ({ state, view, commands }) => {
              const collapsibleList = getCollapsibleListByItemId(state, nodeId);
              if (!collapsibleList) {
                return false;
              }
              const collapsibleListItem = findNodeById(state, nodeId)[0].node;
              const isListItemExpanded = collapsibleListItem.attrs.isExpanded;
              const items = getCollapsibleListItems(state, collapsibleList.attrs.id);
              if (collapsibleList.attrs.expandOnlyOne) {
                const expandedItems = items.filter(
                  (node) => node.attrs.isExpanded === true && node.attrs.id !== nodeId
                );
                if (expandedItems.length > 0 && !isListItemExpanded) {
                  expandedItems.forEach(
                    (i) => updateListItemAttributes(commands, i, { isExpanded: false })
                  );
                }
              }
              updateListItemAttributes(commands, collapsibleListItem, {
                isExpanded: !isListItemExpanded
              });
              view.dispatch(state.tr);
              return true;
            }
          };
        },
        addProseMirrorPlugins() {
          return [
            new Plugin({
              key: collapsibleStateManagerPlugin,
              props: {
                decorations: ({ doc }) => {
                  const decorations = [];
                  doc.descendants((node, pos) => {
                    var _a, _b, _c;
                    const $pos = doc.resolve(pos);
                    const collapsibleListAncestorNode = findParentNodeClosestToPos(
                      $pos,
                      (node2) => {
                        var _a2;
                        return ((_a2 = node2.type) == null ? void 0 : _a2.name) === TIPTAP_COLLAPSIBLE_LIST_TYPE;
                      }
                    );
                    if (((_a = node.type) == null ? void 0 : _a.name) !== TIPTAP_COLLAPSIBLE_LIST_TYPE && !collapsibleListAncestorNode) {
                      return false;
                    }
                    if (collapsibleListAncestorNode) {
                      if (((_b = node == null ? void 0 : node.attrs.textStyle) == null ? void 0 : _b.textAlignment) && ((_c = node == null ? void 0 : node.attrs.textStyle) == null ? void 0 : _c.textAlignment) !== TextStyle_TextAlignment.AUTO) {
                        return;
                      }
                      decorations.push(
                        Decoration.node(pos, pos + node.nodeSize, {
                          "data-text-align": getAlignmentFromDirection(
                            collapsibleListAncestorNode == null ? void 0 : collapsibleListAncestorNode.node.attrs.direction
                          )
                        })
                      );
                    }
                  });
                  return DecorationSet.create(doc, decorations);
                }
              }
            })
          ];
        }
      };
    }
  }),
  createRicosNodeExtension({
    groups: ["custom-styles", "node-container"],
    type: "react-node",
    reconfigure: (config, _extensions, _props, settings) => __spreadProps(__spreadValues({}, config), {
      addOptions: () => settings
    }),
    Component: CollapsibleListItem,
    name: TIPTAP_COLLAPSIBLE_ITEM_TYPE,
    createExtensionConfig() {
      return {
        name: this.name,
        group: TIPTAP_COLLAPSIBLE_ITEM_TYPE,
        draggable: true,
        content: `${TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE} ${TIPTAP_COLLAPSIBLE_ITEM_BODY_TYPE}`,
        addAttributes: () => ({
          isExpanded: { default: true }
        })
      };
    }
  }),
  createRicosNodeExtension({
    groups: ["custom-styles", "node-container"],
    type: "react-node",
    reconfigure: (config, _extensions, _props, settings) => __spreadProps(__spreadValues({}, config), {
      addOptions: () => settings
    }),
    Component: CollapsibleListItemTitle,
    name: TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE,
    placeholder: {
      predicate: ({ doc, pos }) => {
        const parentNode = doc.resolve(pos).parent;
        return parentNode.childCount === 1 && parentNode.type.name === TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE;
      },
      content: "CollapsibleList_ShownText_Placeholder",
      alwaysVisible: true
    },
    createExtensionConfig() {
      return {
        name: this.name,
        group: TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE,
        content: "block"
      };
    }
  }),
  createRicosNodeExtension({
    groups: ["custom-styles", "node-container"],
    type: "react-node",
    reconfigure: (config, _extensions, _props, settings) => __spreadProps(__spreadValues({}, config), {
      addOptions: () => settings
    }),
    Component: CollapsibleListItemBody,
    name: TIPTAP_COLLAPSIBLE_ITEM_BODY_TYPE,
    placeholder: {
      predicate: ({ doc, pos }) => {
        const parentNode = doc.resolve(pos).parent;
        return parentNode.childCount === 1 && parentNode.type.name === TIPTAP_COLLAPSIBLE_ITEM_BODY_TYPE;
      },
      content: "CollapsibleList_CollapsedText_Placeholder",
      alwaysVisible: true
    },
    createExtensionConfig() {
      return {
        name: this.name,
        group: TIPTAP_COLLAPSIBLE_ITEM_BODY_TYPE,
        content: "block+",
        addAttributes: () => ({
          isExpanded: { default: true }
        })
      };
    }
  })
];

export { tiptapExtensions };
//# sourceMappingURL=tiptap.js.map
